import React from "react";
import { Dashboard as DashboardIcon, Group as GroupIcon, AccountTree as AccountTreeIcon, People as PeopleIcon, Security as SecurityIcon, Person as PersonIcon, ShoppingCart as ShoppingCartIcon, Business as BusinessIcon, CalendarToday as CalendarIcon, Folder as FolderIcon, Build as BuildIcon, Language as LanguageIcon, Hub as HubIcon, BusinessCenter as BusinessCenterIcon, RocketLaunch as RocketLaunchIcon, Mail as MailIcon, ShoppingBasket as ShoppingBasketIcon, Error as ErrorIcon, Assignment as AssignmentIcon, Wifi as WifiIcon, Book as BookIcon, AccountBalance as AccountBalanceIcon, Inventory as InventoryIcon, CheckCircle as CheckCircleIcon, Store as StoreIcon, Factory as FactoryIcon, Settings as SettingsIcon, Apps as AppsIcon, Description as DescriptionIcon, Policy as PolicyIcon, PersonAdd as PersonAddIcon, Work as WorkIcon, List as ListIcon, AddBusiness as AddBusinessIcon, CorporateFare as DepartmentIcon, LocationOn as LocationIcon, AccessTime as AccessTimeIcon, EventNote as EventNoteIcon, Assessment as AssessmentIcon } from "@mui/icons-material";
import { useMenuPermissions } from "@/__core/permissions/hooks/useMenuPermissions";

// Helper function to get permission based on path
const getPermissionByPath = (path: string): Array<{resource: string, action: string}> => {
  const pathPermissionMap: Record<string, Array<{resource: string, action: string}>> = {
    '/employees': [{ resource: 'employees', action: 'read' }],
    '/organization': [{ resource: 'departments', action: 'read' }],
    '/users': [{ resource: 'users', action: 'read' }],
    '/sale-orders': [{ resource: 'orders', action: 'read' }],
    '/customers': [{ resource: 'customer', action: 'read' }],
    '/item': [{ resource: 'inventory.item', action: 'read' }],
    '/inventory/items': [{ resource: 'inventory.item', action: 'read' }],
    '/inventory/purchasing': [{ resource: 'inventory.purchase', action: 'read' }],
    '/bom': [{ resource: 'inventory.item', action: 'read' }],
    '/suppliers': [{ resource: 'supplier', action: 'read' }],
    '/project': [{ resource: 'tasks.task', action: 'read' }],
    '/purchase-order': [{ resource: 'orders', action: 'read' }],
    '/issue': [{ resource: 'inventory.issue', action: 'read' }],
    '/lead': [{ resource: 'customer', action: 'read' }],
    '/task': [{ resource: 'tasks.task', action: 'read' }],
    '/contacts': [{ resource: 'customer', action: 'read' }],
    '/profit-loss': [{ resource: 'transaction', action: 'read' }],
    '/finance/report': [
      { resource: 'transaction', action: 'view_financial_report' },
      { resource: 'reports', action: 'view_financial_report' }
    ],
    '/finance/transactions': [
      { resource: 'transaction', action: 'read' },
      { resource: 'transaction', action: 'create' },
      { resource: 'transaction', action: 'approve_transaction' }
    ],
    '/finance/wallets': [{ resource: 'transactionwallet', action: 'read' }],
    '/accounts': [{ resource: 'transactionwallet', action: 'read' }],
    '/stock': [{ resource: 'inventory.stockbalance', action: 'read' }],
    '/inventory/stock-balance': [{ resource: 'inventory.stockbalance', action: 'read' }],
    '/inventory/stock-transaction': [{ resource: 'inventory.stocktransaction', action: 'read' }],
    '/inventory/stores': [{ resource: 'inventory.store', action: 'read' }],
    '/inventory/requests': [{ resource: 'inventory.materialrequest', action: 'read' }],
    '/inventory/receipts': [{ resource: 'inventory.receipt', action: 'read' }],
    '/inventory/allocation': [{ resource: 'inventory.allocation', action: 'read' }],
    '/inventory/issues': [{ resource: 'inventory.issue', action: 'read' }],
    '/inventory/stocktakes': [{ resource: 'inventory.stocktake', action: 'read' }],
    '/inventory/reports': [
      { resource: 'reports', action: 'view_sales_report' },
      { resource: 'reports', action: 'view_delivery_report' },
      { resource: 'reports', action: 'view_income_expense_report' }
    ],
    '/tasks/my': [{ resource: 'tasks.task', action: 'read' }],
    '/todo': [{ resource: 'tasks.task', action: 'read' }],
    '/crm': [{ resource: 'customer', action: 'read' }],
    '/selling': [{ resource: 'orders', action: 'read' }],
    '/buying': [{ resource: 'orders', action: 'read' }],
    '/settings': [{ resource: 'settings.printconfig', action: 'read' }],
    '/timekeepings': [{ resource: 'timekeepings', action: 'read' }],
    '/timekeepings/detail': [{ resource: 'timekeepings', action: 'read' }],
    '/timekeepings/timesheets': [{ resource: 'timesheets', action: '*' }],
    '/order-progress': [{ resource: 'orders.orderprogress', action: 'read' }],
    '/reports': [
      { resource: 'reports', action: 'view_sales_report' },
      { resource: 'reports', action: 'view_delivery_report' },
      { resource: 'reports', action: 'view_income_expense_report' }
    ],
  };

  return pathPermissionMap[path] || [{ resource: 'users', action: 'read' }];
};

// Permission checking functions
export const checkMenuItemPermission = (item: MenuItem, hasPermission: (resource: string, action: string) => boolean, hasRole: (roleName: string) => boolean, hasAnyRole: (roleNames: string[]) => boolean): boolean => {
  // Kiểm tra role global - có toàn quyền (kiểm tra cả role_name và role_level)
  const hasGlobal = hasRole('global');
  const hasGlobalAdmin = hasRole('global_admin');
  
  // Also check for other possible global role variations
  const hasAnyGlobalRole = hasAnyRole(['global', 'global_admin', 'Global', 'Global Admin', 'GLOBAL']);
  
  
  // Global users have access to everything - return immediately
  if (hasGlobal || hasGlobalAdmin || hasAnyGlobalRole) {
    return true;
  }

  // Debug log for customer menu (only log once per session)
  if (item.id === 'customer' && !(window as any).customerMenuDebugLogged) {
   
    (window as any).customerMenuDebugLogged = true;
  }

  // Debug log for CRM menu items (only log once per session)
  if ((item.id === 'crm' || item.id === 'lead' || item.id === 'contacts') && !(window as any).crmMenuDebugLogged) {
    (window as any).crmMenuDebugLogged = true;
  }

  // Nếu không có permission requirements, thử lấy permission từ path
  if (!item.permissions && !item.requiredRole) {
    if (item.path) {
      const pathPermissions = getPermissionByPath(item.path);
      return pathPermissions.some(({ resource, action }) => hasPermission(resource, action));
    }
    return false;
  }

  // Kiểm tra permissions
  if (item.permissions && item.permissions.length > 0) {
    const hasRequiredPermissions = item.permissions.some(({ resource, action }) => hasPermission(resource, action));
    if (!hasRequiredPermissions) {
      return false;
    }
  }

  // Kiểm tra roles
  if (item.requiredRole && item.requiredRole.length > 0) {
    const hasRequiredRole = hasAnyRole(item.requiredRole);
    if (!hasRequiredRole) {
      return false;
    }
  }

  return true;
};

export const checkMenuSectionPermission = (section: MenuSection, hasPermission: (resource: string, action: string) => boolean, hasRole: (roleName: string) => boolean, hasAnyRole: (roleNames: string[]) => boolean): boolean => {
  // Kiểm tra role global - có toàn quyền
  const hasGlobal = hasRole('global');
  const hasGlobalAdmin = hasRole('global_admin');
  
  // Also check for other possible global role variations
  const hasAnyGlobalRole = hasAnyRole(['global', 'global_admin', 'Global', 'Global Admin', 'GLOBAL']);
  
  
  // Global users have access to everything - return immediately
  if (hasGlobal || hasGlobalAdmin || hasAnyGlobalRole) {
    return true;
  }

  // Debug log for sales section (only log once per session)
  if (section.id === 'sales' && !(window as any).salesSectionDebugLogged) {
   
    (window as any).salesSectionDebugLogged = true;
  }

  // Debug log for CRM section (only log once per session)
  if (section.id === 'crm' && !(window as any).crmSectionDebugLogged) {
    (window as any).crmSectionDebugLogged = true;
  }

  // Nếu không có permission requirements, thử lấy permission từ items
  if (!section.permissions && !section.requiredRole) {
    // Nếu có items, kiểm tra permission của items
    if (section.items && section.items.length > 0) {
      return section.items.some(item => checkMenuItemPermission(item, hasPermission, hasRole, hasAnyRole));
    }
    return false;
  }

  // Kiểm tra permissions - section hiển thị nếu user có ÍT NHẤT MỘT permission
  if (section.permissions && section.permissions.length > 0) {
    const hasRequiredPermissions = section.permissions.some(({ resource, action }) => hasPermission(resource, action));
    if (!hasRequiredPermissions) {
      return false;
    }
  }

  // Kiểm tra roles
  if (section.requiredRole && section.requiredRole.length > 0) {
    const hasRequiredRole = hasAnyRole(section.requiredRole);
    if (!hasRequiredRole) {
      return false;
    }
  }

  return true;
};

export const filterMenuItems = (items: MenuItem[], hasPermission: (resource: string, action: string) => boolean, hasRole: (roleName: string) => boolean, hasAnyRole: (roleNames: string[]) => boolean): MenuItem[] => {
  const clonedItems = items.map((item) => ({
    ...item,
    dropdown: item.dropdown ? [...item.dropdown] : undefined,
  }));

  const filteredItems = clonedItems.filter(item => {
    // Kiểm tra permission của item
    if (!checkMenuItemPermission(item, hasPermission, hasRole, hasAnyRole)) {
      return false;
    }

    // Nếu có dropdown, filter dropdown items
    if (item.dropdown && item.dropdown.length > 0) {
      const filteredDropdown = filterMenuItems(item.dropdown, hasPermission, hasRole, hasAnyRole);
      if (filteredDropdown.length === 0) {
        return false;
      }
      item.dropdown = filteredDropdown;
    }

    return true;
  });

  return filteredItems;
};

export const filterMenuSections = (sections: MenuSection[], hasPermission: (resource: string, action: string) => boolean, hasRole: (roleName: string) => boolean, hasAnyRole: (roleNames: string[]) => boolean): MenuSection[] => {
  return sections
    .map((section) => ({
      ...section,
      items: [...section.items],
    }))
    .filter((section) => {
      if (!checkMenuSectionPermission(section, hasPermission, hasRole, hasAnyRole)) {
        return false;
      }

      const filteredItems = filterMenuItems(section.items, hasPermission, hasRole, hasAnyRole);
      if (filteredItems.length === 0) {
        return false;
      }

      section.items = filteredItems;
      return true;
    });
};

// Base interfaces
export interface MenuItem {
  id: string;
  text: string;
  path?: string;
  icon?: React.ReactElement;
  dropdown?: MenuItem[];
  permissions?: Array<{resource: string, action: string}>;
  requiredRole?: string[];
}

export interface MenuSection {
  id: string;
  title?: string;
  showSection?: boolean;
  items: MenuItem[];
  permissions?: Array<{resource: string, action: string}>;
  requiredRole?: string[];
}

// Main sidebar sections (for /main page)
export const MAIN_SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: "main",
    title: "Home",
    items: [
      {
        id: "dashboard",
        text: "Dashboard",
        path: "/main",
        icon: <DashboardIcon sx={{ fontSize: 20 }} />,
        permissions: [],
      },
      {
        id: "manage_orders",
        text: "Quản lý đơn hàng",
        path: "/manage-orders",
        icon: <ShoppingCartIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      }, 
      {
        id: "manage_order_details",
        text: "QLDH Chi tiết",
        path: "/manage-order-details",
        icon: <GroupIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      },
      {
        id: "manage_progress_delivery",
        text: "Tiến độ giao hàng",
        path: "/manage-progress-delivery",
        icon: <EventNoteIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      },
      {
        id: "manage_progress_production",
        text: "Tiến độ sản xuất",
        path: "/manage-progress-production",
        icon: <BuildIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      },
      
    ],
  },
  {
    id: "user-resources",
    title: "Users",
    permissions: [
      { resource: 'users', action: 'read' },
      { resource: 'roles', action: 'read' }
    ],
    items: [
      {
        id: "users",
        text: "Người dùng",
        path: "/users",
        icon: <PeopleIcon />,
        permissions: [{ resource: 'users', action: 'read' }],
      },
      {
        id: "roles",
        text: "Vai trò",
        path: "/users/roles",
        icon: <SecurityIcon />,
        permissions: [{ resource: 'roles', action: 'read' }],
      },
    ],
  },
  {
    id: "human-resources",
    title: "Nhân sự",
    permissions: [
      { resource: 'employees', action: 'read' },
      { resource: 'departments', action: 'read' },
      { resource: 'employees.fixedsalary', action: 'read' },
      { resource: 'employees.insurance', action: 'read' }
    ],
    items: [
      {
        id: "employees",
        text: "Nhân viên",
        path: "/employees",
        icon: <GroupIcon />,
        permissions: [{ resource: 'employees', action: 'read' }],
      },
      {
        id: "organization",
        text: "Tổ chức",
        path: "/organization",
        icon: <AccountTreeIcon />,
        permissions: [{ resource: 'departments', action: 'read' }],
      },
      {
        id: "fixed-salary",
        text: "Lương cố định",
        path: "/fixed-salary",
        icon: <AccountBalanceIcon />,
        permissions: [{ resource: 'employees.fixedsalary', action: 'read' }],
      },
      {
        id: "social-insurance",
        text: "Bảo hiểm",
        path: "/insurance",
        icon: <PolicyIcon />,
        permissions: [{ resource: 'employees.insurance', action: 'read' }],
      },
    ],
  },
  {
    id: "timekeeping",
    title: "Chấm công",
    items: [
      {
        id: "timekeeping-list",
        text: "Bảng chấm công",
        path: "/timekeepings",
        icon: <AccessTimeIcon />,
        permissions: [
          { resource: 'timekeepings', action: 'create' },
          { resource: 'timekeepings', action: 'update' },
        ],
      },
      {
        id: "timekeeping-detail",
        text: "Bảng chi tiết chấm công",
        path: "/timekeepings/detail",
        icon: <EventNoteIcon />,
        permissions: [{ resource: 'timekeepings', action: 'read' }],
      },
      {
        id: "timesheets",
        text: "Bảng lương",
        path: "/timekeepings/timesheets",
        icon: <AccountBalanceIcon />,
        permissions: [{ resource: 'timesheets', action: '*' }],
      },
    ],
  },
  {
    id: "finance",
    title: "Tài chính",
    permissions: [
      { resource: 'transaction', action: 'read' },
      { resource: 'transaction', action: 'create' },
      { resource: 'transaction', action: 'approve_transaction' },
    ],
    items: [
      {
        id: "finance-report",
        text: "Báo cáo tài chính",
        path: "/finance/report",
        icon: <AssessmentIcon />,
        permissions: [
          { resource: 'transaction', action: 'view_financial_report' },
          { resource: 'reports', action: 'view_financial_report' }
        ],
      },
      {
        id: "finance-income-expense",
        text: "Thu chi",
        path: "/finance/transactions",
        icon: <AccountBalanceIcon />,
        permissions: [
          { resource: 'transaction', action: 'read' },
          { resource: 'transaction', action: 'create' },
          { resource: 'transaction', action: 'approve_transaction' }
        ],
      },
    ],
  },
  {
    id: "sales",
    title: "Bán hàng",
    permissions: [
      { resource: 'orders', action: 'read' },
      { resource: 'customer', action: 'read' }
    ],
    items: [
      {
        id: "sales-order",
        text: "Đơn hàng bán",
        path: "/sale-orders",
        icon: <ShoppingCartIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      },
      {
        id: "customer",
        text: "Khách hàng",
        path: "/customers",
        icon: <PersonIcon />,
        permissions: [{ resource: 'customer', action: 'read' }],
      },
      {
        id: "order-progress",
        text: "Tiến độ đơn hàng",
        path: "/order-progress",
        icon: <EventNoteIcon />,
        permissions: [{ resource: 'orders.orderprogress', action: 'read' }],
      }
     
    ],
  },
  {
    id: "reports",
    title: "Báo cáo",
    permissions: [
      { resource: 'reports', action: 'view_sales_report' },
      { resource: 'reports', action: 'view_delivery_report' },
      { resource: 'reports', action: 'view_income_expense_report' }
    ],
    items: [
      {
        id: "reports-overview",
        text: "Báo cáo tổng hợp",
        path: "/reports",
        icon: <AssessmentIcon />,
        permissions: [
          { resource: 'reports', action: 'view_sales_report' },
          { resource: 'reports', action: 'view_delivery_report' },
          { resource: 'reports', action: 'view_income_expense_report' }
        ],
      },
    ],
  },
  {
    id: "purchase",
    title: "Mua hàng",
    items: [
      {
        id: "purchase-order",
        text: "Đơn hàng mua",
        path: "/inventory/purchasing",
        icon: <ShoppingCartIcon />,
        permissions: [{ resource: 'inventory.purchase', action: 'read' }],
      },
      {
        id: "supplier",
        text: "Nhà cung cấp",
        path: "/suppliers",
        icon: <BusinessCenterIcon />,
        permissions: [{ resource: 'supplier', action: 'read' }],
      },
    ],
  },
  {
    id: "inventory",
    title: "Kho",
    items: [
      {
        id: "inventory-dashboard",
        text: "Tổng quan kho",
        path: "/inventory",
        icon: <InventoryIcon />,
        permissions: [{ resource: 'inventory.item', action: 'read' }],
      },
      {
        id: "inventory-items",
        text: "Danh mục vật tư",
        path: "/inventory/items",
        icon: <InventoryIcon />,
        permissions: [{ resource: 'inventory.item', action: 'read' }],
      },
      {
        id: "stock-balance",
        text: "Tồn kho",
        path: "/inventory/stock-balance",
        icon: <AssessmentIcon />,
        permissions: [{ resource: 'inventory.stockbalance', action: 'read' }],
      },
      {
        id: "stock-transaction",
        text: "Giao dịch kho",
        path: "/inventory/stock-transaction",
        icon: <AssignmentIcon />,
        permissions: [{ resource: 'inventory.stocktransaction', action: 'read' }],
      },
      {
        id: "inventory-stores",
        text: "Quản lý kho",
        path: "/inventory/stores",
        icon: <StoreIcon />,
        permissions: [{ resource: 'inventory.store', action: 'read' }],
      },
      {
        id: "inventory-requests",
        text: "Đề xuất vật tư",
        path: "/inventory/requests",
        icon: <DescriptionIcon />,
        permissions: [{ resource: 'inventory.materialrequest', action: 'read' }],
      },
      {
        id: "inventory-receipts",
        text: "Nhập kho",
        path: "/inventory/receipts",
        icon: <AddBusinessIcon />,
        permissions: [{ resource: 'inventory.receipt', action: 'read' }],
      },
      {
        id: "inventory-allocation",
        text: "Gán vật tư",
        path: "/inventory/allocation",
        icon: <HubIcon />,
        permissions: [{ resource: 'inventory.allocation', action: 'read' }],
      },
      {
        id: "inventory-issues",
        text: "Xuất kho",
        path: "/inventory/issues",
        icon: <ShoppingBasketIcon />,
        permissions: [{ resource: 'inventory.issue', action: 'read' }],
      },
      {
        id: "inventory-stocktakes",
        text: "Kiểm kê",
        path: "/inventory/stocktakes",
        icon: <CheckCircleIcon />,
        permissions: [{ resource: 'inventory.stocktake', action: 'read' }],
      },
      {
        id: "inventory-reports",
        text: "Báo cáo kho",
        path: "/inventory/reports",
        icon: <AssessmentIcon />,
        permissions: [
          { resource: 'reports', action: 'view_sales_report' },
          { resource: 'reports', action: 'view_delivery_report' },
          { resource: 'reports', action: 'view_income_expense_report' }
        ],
      },
    ],
  },
  {
    id: "manufacturing",
    title: "Sản xuất",
    permissions: [
      { resource: 'production', action: 'read' }
    ],
    items: [
      {
        id: "production-order",
        text: "Lệnh sản xuất",
        path: "/production-order",
        icon: <BuildIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      },
      {
        id: "manufacturing",
        text: "Sản xuất",
        path: "/manufacturing",
        icon: <FactoryIcon />,
        permissions: [{ resource: 'orders', action: 'read' }],
      },
    ],
  },
  {
    id: "tasks",
    title: "Nhiệm vụ",
    showSection: false,
    permissions: [
      { resource: 'tasks.task', action: 'read' }
    ],
    items: [
      {
        id: "my-tasks",
        text: "Nhiệm vụ",
        path: "/tasks/my",
        icon: <AssignmentIcon />,
        permissions: [{ resource: 'tasks.task', action: 'read' }],
      },
   
    ],
  },
  /*{
    id: "project",
    title: "Dự án",
    permissions: [
      { resource: 'tasks.task', action: 'read' }
    ],
    items: [
      {
        id: "project",
        text: "Dự án",
        path: "/project",
        icon: <RocketLaunchIcon />,
        permissions: [{ resource: 'tasks.task', action: 'read' }],
      },
      {
        id: "task",
        text: "Công việc",
        path: "/task",
        icon: <AssignmentIcon />,
        permissions: [{ resource: 'tasks.task', action: 'read' }],
      },
      {
        id: "todo",
        text: "ToDo",
        path: "/todo",
        icon: <CheckCircleIcon />,
        permissions: [{ resource: 'tasks.task', action: 'read' }],
      },
    ],
  },
   {
    id: "crm",
    title: "CRM",
    permissions: [
      { resource: 'customer', action: 'read' },
      { resource: 'lead', action: 'read' },
      { resource: 'contacts', action: 'read' }
    ],
    items: [
      {
        id: "crm",
        text: "CRM",
        path: "/crm",
        icon: <PersonIcon />,
        permissions: [{ resource: 'customer', action: 'read' }],
      },
      {
        id: "lead",
        text: "Tiềm năng",
        path: "/lead",
        icon: <WifiIcon />,
        permissions: [{ resource: 'lead', action: 'read' }],
      },
      {
        id: "contacts",
        text: "Danh bạ",
        path: "/contacts",
        icon: <BookIcon />,
        permissions: [{ resource: 'contacts', action: 'read' }],
      },
    ],
  },
  {
    id: "communication",
    title: "Giao tiếp",
    permissions: [
      { resource: 'email', action: 'read' }
    ],
    items: [
      {
        id: "email-inbox",
        text: "Email",
        path: "/email-inbox",
        icon: <MailIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
      {
        id: "meeting",
        text: "Họp",
        path: "/meeting",
        icon: <FolderIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
    ],
  },
  {
    id: "tools",
    title: "Công cụ",
    permissions: [
      { resource: 'tools', action: 'read' }
    ],
    items: [
      {
        id: "tools",
        text: "Công cụ",
        path: "/tools",
        icon: <CalendarIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
      {
        id: "file-manager",
        text: "Quản lý file",
        path: "/file-manager",
        icon: <FolderIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
    ],
  },
  {
    id: "quality",
    title: "Chất lượng",
    permissions: [
      { resource: 'issue', action: 'read' }
    ],
    items: [
      {
        id: "issue",
        text: "Vấn đề",
        path: "/issue",
        icon: <ErrorIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
    ],
  }, */
  {
    id: "settings",
    title: "Cài đặt",
    showSection: false,
    permissions: [
      { resource: 'settings.printconfig', action: 'read' }
    ],
    items: [
      {
        id: "settings",
        text: "Cài đặt",
        path: "/settings",
        icon: <SettingsIcon />,
        permissions: [{ resource: 'settings.printconfig', action: 'read' }],
      },
    ],
  },
];

// Apps dropdown items (for Apps menu)
export const APPS_DROPDOWN_ITEMS: MenuItem[] = [
  {
    id: "human-resources",
    text: "Nhân sự",
    path: "/employees",
    icon: <GroupIcon />,
  },
  {
    id: "organization",
    text: "Tổ chức",
    path: "/organization",
    icon: <AccountTreeIcon />,
  },
  {
    id: "users",
    text: "Users",
    path: "/users",
    icon: <PeopleIcon />,
  },
  {
    id: "sales-order",
    text: "Sales Order",
    path: "/sale-orders",
    icon: <ShoppingCartIcon />,
  },
  {
    id: "customer",
    text: "Customer",
    path: "/customers",
    icon: <PersonIcon />,
  },
  {
    id: "item",
    text: "Item",
    path: "/item",
    icon: <InventoryIcon />,
  },
  {
    id: "tools",
    text: "Tools",
    path: "/tools",
    icon: <CalendarIcon />,
  },
  {
    id: "meeting",
    text: "Meeting",
    path: "/meeting",
    icon: <FolderIcon />,
  },
  {
    id: "file-manager",
    text: "File Manager",
    path: "/file-manager",
    icon: <FolderIcon />,
  },
  {
    id: "bom",
    text: "BOM",
    path: "/bom",
    icon: <BuildIcon />,
  },
  {
    id: "website",
    text: "Website",
    path: "/website",
    icon: <LanguageIcon />,
  },
  {
    id: "integrations",
    text: "Integrations",
    path: "/integrations",
    icon: <HubIcon />,
  },
  {
    id: "supplier",
    text: "Supplier",
    path: "/suppliers",
    icon: <BusinessCenterIcon />,
  },
  {
    id: "project",
    text: "Project",
    path: "/project",
    icon: <RocketLaunchIcon />,
  },
  {
    id: "email-inbox",
    text: "Email Inbox",
    path: "/email-inbox",
    icon: <MailIcon />,
  },
  {
    id: "purchase-order",
    text: "Purchase Order",
    path: "/purchase-order",
    icon: <ShoppingBasketIcon />,
  },
  {
    id: "issue",
    text: "Issue",
    path: "/issue",
    icon: <ErrorIcon />,
  },
  {
    id: "production-order",
    text: "Production Order",
    path: "/production-order",
    icon: <BuildIcon />,
  },
  {
    id: "lead",
    text: "Lead",
    path: "/lead",
    icon: <WifiIcon />,
  },
  {
    id: "task",
    text: "Task",
    path: "/task",
    icon: <AssignmentIcon />,
  },
  {
    id: "contacts",
    text: "Contacts",
    path: "/contacts",
    icon: <BookIcon />,
  },
  {
    id: "profit-loss",
    text: "Profit and Loss Stat...",
    path: "/profit-loss",
    icon: <AssessmentIcon />,
  },
  {
    id: "finance-report",
    text: "Finance Report",
    path: "/finance/report",
    icon: <DashboardIcon />,
  },
  {
    id: "finance-income-expense",
    text: "Income & Expense",
    path: "/finance/transactions",
    icon: <AccountBalanceIcon />,
  },
  {
    id: "finance-transaction-types",
    text: "Transaction Types",
    path: "/finance/transaction-types",
    icon: <ListIcon />,
  },
  {
    id: "finance-wallets",
    text: "Wallets",
    path: "/finance/wallets",
    icon: <AccountBalanceIcon />,
    permissions: [{ resource: 'transactionwallet', action: 'read' }],
  },
  {
    id: "accounts",
    text: "Accounts",
    path: "/accounts",
    icon: <AccountBalanceIcon />,
    permissions: [{ resource: 'transactionwallet', action: 'read' }],
  },
  {
    id: "timekeeping-list",
    text: "Bảng chấm công",
    path: "/timekeepings",
    icon: <AccessTimeIcon />,
    permissions: [
      { resource: 'timekeepings', action: 'create' },
      { resource: 'timekeepings', action: 'update' },
    ],
  },
  {
    id: "timekeeping-detail",
    text: "Bảng chi tiết chấm công",
    path: "/timekeepings/detail",
    icon: <EventNoteIcon />,
    permissions: [{ resource: 'timekeepings', action: 'read' }],
  },
  {
    id: "stock",
    text: "Stock",
    path: "/inventory",
    icon: <InventoryIcon />,
  },
  {
    id: "todo",
    text: "ToDo",
    path: "/todo",
    icon: <CheckCircleIcon />,
  },
  {
    id: "crm",
    text: "CRM",
    path: "/crm",
    icon: <PersonIcon />,
  },
  {
    id: "selling",
    text: "Selling",
    path: "/selling",
    icon: <StoreIcon />,
  },
  {
    id: "buying",
    text: "Buying",
    path: "/buying",
    icon: <ShoppingCartIcon />,
  },
  {
    id: "manufacturing",
    text: "Manufacturing",
    path: "/manufacturing",
    icon: <FactoryIcon />,
  },
  {
    id: "settings",
    text: "Settings",
    path: "/settings",
    icon: <SettingsIcon />,
  },
];

// Main sidebar sections (for regular pages)
export const SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: "main",
    permissions: [
      { resource: 'system', action: 'read' }
    ],
    items: [
      {
        id: "apps",
        text: "Apps",
        icon: <AppsIcon />,
        dropdown: APPS_DROPDOWN_ITEMS,
        permissions: [{ resource: 'system', action: 'read' }],
      },
      {
        id: "dashboard",
        text: "Dashboard",
        path: "/dashboard",
        icon: <DashboardIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
    ],
  },
  ...MAIN_SIDEBAR_SECTIONS,
  {
    id: "documents",
    title: "Tài liệu",
    permissions: [
      { resource: 'documents', action: 'read' }
    ],
    items: [
      {
        id: "documents",
        text: "Tài liệu",
        path: "/documents",
        icon: <DescriptionIcon />,
        permissions: [{ resource: 'documents', action: 'read' }],
      },
    ],
  },
  {
    id: "policies",
    title: "Quy tắc & Chính sách",
    permissions: [
      { resource: 'policies', action: 'read' }
    ],
    items: [
      {
        id: "work-rules",
        text: "Quy tắc làm việc",
        path: "/work-rules",
        icon: <DescriptionIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
      {
        id: "career-policies",
        text: "Chính sách nghề nghiệp",
        path: "/career-policies",
        icon: <PolicyIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
      {
        id: "information-security",
        text: "Bảo mật thông tin",
        path: "/information-security",
        icon: <SecurityIcon />,
        permissions: [{ resource: 'system', action: 'read' }],
      },
    ],
  },
];

// Employees module sidebar
export const EMPLOYEES_SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: "main",
    items: [
      {
        id: "dashboard",
        text: "Dashboard",
        path: "/employees/dashboard",
        icon: <DashboardIcon />,
      },
      {
        id: "employees",
        text: "Danh sách nhân viên",
        path: "/employees",
        icon: <PeopleIcon />,
      },
      {
        id: "add-employee",
        text: "Thêm nhân viên",
        path: "/employees/add",
        icon: <PersonAddIcon />,
      },
    ],
  },
  {
    id: "management",
    title: "Quản lý",
    items: [
      {
        id: "contracts",
        text: "Hợp đồng",
        path: "/employees/contracts",
        icon: <DescriptionIcon />,
      },
      {
        id: "careers",
        text: "Sự nghiệp",
        path: "/employees/careers",
        icon: <WorkIcon />,
      },
      {
        id: "payrolls",
        text: "Lương thưởng",
        path: "/employees/payrolls",
        icon: <AccountBalanceIcon />,
      },
      {
        id: "assignments",
        text: "Phân công",
        path: "/employees/assignments",
        icon: <AssignmentIcon />,
      },
    ],
  },
];

// Organizations module sidebar
export const ORGANIZATIONS_SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: "main",
    items: [
      {
        id: "dashboard",
        text: "Dashboard",
        path: "/organization/dashboard",
        icon: <DashboardIcon />,
      },
      {
        id: "organization",
        text: "Tổ chức",
        path: "/organization",
        icon: <AccountTreeIcon />,
      },
      {
        id: "companies",
        text: "Công ty",
        path: "/organization/companies",
        icon: <BusinessIcon />,
      },
      {
        id: "departments",
        text: "Phòng ban",
        path: "/organization/departments",
        icon: <DepartmentIcon />,
      },
    ],
  },
  {
    id: "management",
    title: "Quản lý",
    items: [
      {
        id: "positions",
        text: "Vị trí",
        path: "/organization/positions",
        icon: <BusinessIcon />,
      },
      {
        id: "job-functions",
        text: "Chức năng công việc",
        path: "/organization/job-functions",
        icon: <ListIcon />,
      },
      {
        id: "addresses",
        text: "Địa chỉ",
        path: "/organization/addresses",
        icon: <LocationIcon />,
      },
    ],
  },
  {
    id: "documents",
    title: "Tài liệu",
    items: [
      {
        id: "documents",
        text: "Tài liệu tổ chức",
        path: "/organization/documents",
        icon: <DescriptionIcon />,
      },
    ],
  },
];

// Hook để lấy menu sections với permissions
export const useMainSidebarSections = (): MenuSection[] => {
  const { hasPermission, hasRole, hasAnyRole } = useMenuPermissions();
  return React.useMemo(
    () => filterMenuSections(MAIN_SIDEBAR_SECTIONS, hasPermission, hasRole, hasAnyRole),
    [hasPermission, hasRole, hasAnyRole]
  );
};

export const useSidebarSections = (): MenuSection[] => {
  const { hasPermission, hasRole, hasAnyRole } = useMenuPermissions();
  return filterMenuSections(SIDEBAR_SECTIONS, hasPermission, hasRole, hasAnyRole);
};

export const useAppsDropdownItems = (): MenuItem[] => {
  const { hasPermission, hasRole, hasAnyRole } = useMenuPermissions();
  return filterMenuItems(APPS_DROPDOWN_ITEMS, hasPermission, hasRole, hasAnyRole);
};

export const useEmployeesSidebarSections = (): MenuSection[] => {
  const { hasPermission, hasRole, hasAnyRole } = useMenuPermissions();
  return filterMenuSections(EMPLOYEES_SIDEBAR_SECTIONS, hasPermission, hasRole, hasAnyRole);
};

export const useOrganizationsSidebarSections = (): MenuSection[] => {
  const { hasPermission, hasRole, hasAnyRole } = useMenuPermissions();
  return filterMenuSections(ORGANIZATIONS_SIDEBAR_SECTIONS, hasPermission, hasRole, hasAnyRole);
};

// Export type aliases for backward compatibility
export type SidebarItem = MenuItem;
export type SidebarSection = MenuSection;
export type MainSidebarItem = MenuItem;
export type MainSidebarSection = MenuSection;
